import React, { useContext, useEffect } from 'react'

import { FormattedMessage } from 'react-intl'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import Flex from 'styled-flex-component'

import { SideBarBlock, SideBarBlockItem } from '@components/UI/SideBar'
import TrackedRoute from '@components/UI/TrackedRoute/TrackedRoute'
import {
  IMobileMenuControlsContextProps,
  MobileMenuControlsContext,
} from '@context/MobileMenuControls'
import { ContentLibraryFilterTypes, EventTrackingNames, PageRoutes } from '@enums'
import { IApplicationState } from '@store'
import { selectContentLibraryFilters } from '@store/me'
import { fetchMediaObjects, IMediaObjectStats, selectMediaObjectsStats } from '@store/mediaObjects'
import { selectContentLibraryFilter } from '@store/router'
import { objectToQueryString } from '@utils'
import { isActiveMenu } from '@utils/urls'

interface IInternalProps extends IMobileMenuControlsContextProps {
  fetchMediaObjects: typeof fetchMediaObjects
  contentLibraryFilters: ReadonlyArray<string>
  contentLibraryStats: IMediaObjectStats
  contentLibraryCurrentFilter: ContentLibraryFilterTypes
}

const ContentLibraryFilters: React.FC<IInternalProps> = ({
  contentLibraryFilters,
  contentLibraryStats,
  hideMobileMenu,
  contentLibraryCurrentFilter,
  fetchMediaObjects: fetchMediaObjectsAction,
}) => {
  useEffect(() => {
    fetchMediaObjectsAction(contentLibraryCurrentFilter)
  }, [])

  const { pathname } = useLocation()

  return (
    <SideBarBlock
      header={
        <Flex justifyBetween alignCenter>
          <FormattedMessage id="contentLibrary.sidebar.header" />
        </Flex>
      }
      items={[
        ...contentLibraryFilters.map((filter: string) => (
          <TrackedRoute
            key={filter}
            eventName={EventTrackingNames.ViewContentLibrary}
            properties={{
              current_filter: `content_library_${filter}`,
              library_size: contentLibraryStats[filter],
            }}
          >
            <SideBarBlockItem
              key={filter}
              to={{
                pathname: PageRoutes.ContentLibrary,
                search: objectToQueryString({ filter }),
              }}
              text={<FormattedMessage id={`contentLibrary.sidebar.filter.${filter}`} />}
              active={isActiveMenu(
                pathname,
                PageRoutes.ContentLibrary,
                contentLibraryCurrentFilter,
                filter,
              )}
              onClick={hideMobileMenu}
              count={contentLibraryStats[filter]}
              elementName={`content-library-sidebar-${filter}`}
            />
          </TrackedRoute>
        )),
      ]}
    />
  )
}

const ConnectedContentLibraryFilters: React.FC = () => {
  const dispatch = useDispatch()
  const mobileMenuControls = useContext(MobileMenuControlsContext)
  const { contentLibraryFilters, contentLibraryCurrentFilter, contentLibraryStats } = useSelector(
    (state: IApplicationState) => ({
      contentLibraryFilters: selectContentLibraryFilters(state),
      contentLibraryCurrentFilter: selectContentLibraryFilter(state),
      contentLibraryStats: selectMediaObjectsStats(state),
    }),
    shallowEqual,
  )

  return (
    <ContentLibraryFilters
      contentLibraryFilters={contentLibraryFilters}
      contentLibraryCurrentFilter={contentLibraryCurrentFilter}
      contentLibraryStats={contentLibraryStats}
      fetchMediaObjects={(filter) => dispatch(fetchMediaObjects(filter))}
      {...mobileMenuControls}
    />
  )
}

export { ContentLibraryFilters }
export default ConnectedContentLibraryFilters
